import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { Button } from './ui/Button';

// "Remove <name>?" — the details panel's inline confirm (#6563, owner ruling G11). Inline,
// not a nested dialog: it sits inside the panel's dialog, and a second dialog role would
// fight it for the focus trap (the prototype's `role="alertdialog"` is one of its bugs:
// `testing/overlay-shells.ts` holds every dialog role to the swept shells). A named
// region, so the confirm is found by its question. Its first button is where focus lands when the panel
// opens at Remove (`data-remove-confirm`, read by `MachinePanel`'s `initialFocus`).
//
// "Its current task goes back in line" is said only when the machine has one. A refusal
// keeps the confirm open with the API's reason, and Remove can be pressed again.

export function MachineRemoveConfirm(props: {
  readonly name: string;
  readonly hasTask: boolean;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}): JSX.Element {
  return (
    <section
      class="machine-panel__confirm machine-panel__confirm--danger"
      aria-labelledby="machine-panel-remove-title"
      aria-describedby="machine-panel-remove-body"
      data-remove-confirm=""
    >
      <h4 id="machine-panel-remove-title" class="machine-panel__confirm-title">
        {t('fleet.panel.remove_title', { name: props.name })}
      </h4>
      <p id="machine-panel-remove-body" class="machine-panel__confirm-body">
        {t(props.hasTask ? 'fleet.panel.remove_body_task' : 'fleet.panel.remove_body')}
      </p>
      <Show when={props.error}>
        {(msg) => (
          <p class="field__error" role="alert">
            {msg()}
          </p>
        )}
      </Show>
      <div class="machine-panel__confirm-actions">
        <Button variant="destructive" disabled={props.busy} onClick={props.onConfirm}>
          {t('fleet.machines.menu_remove')}
        </Button>
        <Button variant="outline" disabled={props.busy} onClick={props.onCancel}>
          {t('fleet.panel.remove_keep')}
        </Button>
      </div>
    </section>
  );
}
